import { useState, useEffect, useCallback } from "react";
import { useSnackbar } from "notistack";
import { Paper, TextField, Box } from "@mui/material";
import ProjectTreeView from "../components/ProjectTreeView";
import Typography from "@mui/material/Typography";
import { observer } from "mobx-react";
import { CalculateStaticAnalysisWithPreBaseline } from "../operations/AnalysisOperations";
import ProjectAnalysis from "../model/ProjectAnalysis";
import TaskAnalysis from "../model/TaskAnalysis";
import AnalysisViewEchart from "../components/AnalysisViewEchart";
import ToggleButton from "@mui/material/ToggleButton";
import { DataGrid } from "@mui/x-data-grid";
import CircularProgress from "@mui/material/CircularProgress";
import { Snackbar } from "@mui/material";
import Alert from "@mui/material/Alert";
import { useProjectStore } from "../model/ProjectStore";
import { useViewState } from "../model/ViewState";
import CollapsiblePanel from "../components/CollapsiblePanel";

const AnalysisTable = ({ selectedTask, view }) => {
  if (!selectedTask.analysis) {
    selectedTask.analysis = [];
  }

  const data = selectedTask.analysis.map((entry) => {
    return generateTableRow(entry);
  });

  const columns = [
    {
      field: "id",
      headerName: "Period",
      width: 118,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "expEarnedValue",
      headerName: "Exp Earn Val",
      width: 160,
      align: "center",
      headerAlign: "center",
      cellClassName: "uneditableExp",
    },
    {
      field: "earnedValue",
      headerName: "Earn Val",
      width: 130,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "earnedSchedule",
      headerName: "Earn Schd",
      width: 145,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "SPI",
      headerName: "SPI(t)",
      width: 122,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "expProgress",
      headerName: "Exp Progress",
      width: 165,
      align: "center",
      headerAlign: "center",
      cellClassName: "uneditableExp",
    },
    {
      field: "progress",
      headerName: "Progress",
      width: 135,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "note",
      headerName: "Note",
      editable: true,
      width: 150,
      align: "center",
      headerAlign: "center",
    },
  ];

  const rows = data.map((row) => ({
    id: row.period,
    expEarnedValue: row.expEarnedValue,
    earnedValue: row.earnedValue,
    earnedSchedule: row.earnedSchedule,
    SPI: row.spi,
    expProgress: row.expProgress,
    progress: row.progress,
    note: row.note,
  }));

  return (
    <div>
      {!view.showAnalysisLoadingMessage ? (
        <Box
          sx={{
            height: 900,
            width: 1425,
            "& .uneditableExp": {
              backgroundColor: "rgba(225, 255, 255, 0)",
              color: "#808080",
              fontStyle: "italic",
            },
          }}
        >
          {selectedTask.id === undefined || selectedTask.id === null ? (
            <Typography>
              Analysis result for the whole project have been generated. Please
              select a task to see the result for the specific Task or Summary
            </Typography>
          ) : (
            <DataGrid
              rows={rows}
              columns={columns}
              rowsPerPageOptions={[15]}
              pageSize={15}
              pagination
            />
          )}
        </Box>
      ) : (
        <div>
          <CircularProgress color="inherit" />
          <Typography>
            Please wait while analysis is being generated based on current data
            and existing baselines, this may take several minutes
          </Typography>
        </div>
      )}
    </div>
  );
};

const PerformanceAndEarnedChart = ({ task, open, prebaselinePeriods }) => {
  if (open) {
    return (
      <AnalysisViewEchart task={task} prebaselinePeriods={prebaselinePeriods} />
    );
  } else {
    return <div />;
  }
};

const generateTableRow = (analysis) => {
  let period = analysis.isPreBase ? `${analysis.period}*` : analysis.period;
  let expEarnedValue = analysis.isPreBase
    ? `${analysis.plannedValue.toFixed(2)}*`
    : analysis.plannedValue.toFixed(2);
  let earnedValue = analysis.isPreBase
    ? `${analysis.earnedValue.toFixed(2)}*`
    : analysis.earnedValue.toFixed(2);
  let earnedSchedule = analysis.isPreBase
    ? `${analysis.earnedSchedule.toFixed(3)}*`
    : analysis.earnedSchedule.toFixed(3);
  let spi = analysis.isPreBase
    ? `${analysis.schedulePerformanceIndexTime.toFixed(3)}*`
    : analysis.schedulePerformanceIndexTime.toFixed(3);
  let expProgress = analysis.isPreBase
    ? `${(analysis.plannedProgress * 100).toFixed(3)}*`
    : (analysis.plannedProgress * 100).toFixed(3);
  let progress = analysis.isPreBase
    ? `${(analysis.actualProgress * 100).toFixed(3)}*`
    : (analysis.actualProgress * 100).toFixed(3);
  let note = analysis.note;

  return {
    period: period,
    expEarnedValue: expEarnedValue,
    earnedValue: earnedValue,
    earnedSchedule: earnedSchedule,
    spi: spi,
    expProgress: expProgress,
    progress: progress,
    note: note,
  };
};

const AnalysisView = () => {
  const { enqueueSnackbar } = useSnackbar();
  const project = useProjectStore();
  const view = useViewState();
  let [treeDrawerOpen, setTreeDrawerOpen] = useState(true);
  let [performanceOpen, setPerformanceOpen] = useState(false);
  let [drawerWidth, setDrawerWidth] = useState(240);
  const [loadingPrebaseline, setloadingPrebaseline] = useState(false);

  let selectedTask = !!view.projectAnalysis
    ? view.projectAnalysis.rootTaskPackage.getAnalysis(view.selectedTaskId)
    : null;

  if (selectedTask == null) {
    selectedTask = new TaskAnalysis();
  }

  useEffect(() => {
    project.checkProjUpdate(project.id, project.currentHash, enqueueSnackbar)
      .catch((error) => {
        enqueueSnackbar(error.message, { variant: "error" });
        if (error.cause === "opaqueredirect") {
          setTimeout(() => {
            window.location.href = "/login";
          }, 1000);
        }
      });

    CalculateStaticAnalysisWithPreBaseline(
      project,
      view,
      enqueueSnackbar,
      (result) => {
        let projectAnalysis = ProjectAnalysis.fromJS(result);
        view.setProjectAnalysis(projectAnalysis);
      }
    );
  }, [enqueueSnackbar, project, view]);

  const handleMouseDown = (e) => {
    document.addEventListener("mouseup", handleMouseUp, true);
    document.addEventListener("mousemove", handleMouseMove, true);
  };

  const handleMouseUp = () => {
    document.removeEventListener("mouseup", handleMouseUp, true);
    document.removeEventListener("mousemove", handleMouseMove, true);
  };

  const handleMouseMove = useCallback((e) => {
    const newWidth = e.clientX - document.getElementById("analysisViewResizableDrawer").offsetLeft;
    if (newWidth > 100 && newWidth < 1000) {
      setDrawerWidth(newWidth);
    }
  }, []);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    view.showAnalysisLoadingMessage = false;
    setloadingPrebaseline(false);
  };

  return (
    <div>
      <div>
        <Typography variant="h4" gutterBottom component="h2">
          Project Analysis
        </Typography>
      </div>
      <div style={{ display: "flex", padding: "5px", minHeight: "800px" }}>
        <div style={{ margin: "5px" }}></div>
        <CollapsiblePanel
          width={drawerWidth}
          setWidth={setDrawerWidth}
          open={treeDrawerOpen}
          onToggle={() => setTreeDrawerOpen(!treeDrawerOpen)}
          menuActions={null}
        >
          <ProjectTreeView
            fromTask={false}
          />
        </CollapsiblePanel>
        <div
          style={{ display: "flex", flexDirection: "row", margin: "0px 8px" }}
        >
          <Paper
            style={{ minWidth: "800px", minHeight: "400px", padding: "8px" }}
          >
            <Typography color="textPrimary" variant="h6">
              Details
            </Typography>
            <TextField
              key={view.selectedTaskId + "_name"}
              id="taskName"
              label="Task Name"
              margin="normal"
              value={selectedTask.name}
              slotProps={{
                input: {
                  readOnly: "true"
                },
                inputLabel: {
                  shrink: "true"
                }
              }}
              variant="standard"
            />
            <div>
              <ToggleButton
                value="PerfAndEVAChart"
                variant="contained"
                selected={performanceOpen}
                size="small"
                onClick={() => setPerformanceOpen(!performanceOpen)}
              >
                Performance & Earned Value Chart
              </ToggleButton>
            </div>
            <PerformanceAndEarnedChart
              task={selectedTask}
              open={performanceOpen}
              prebaselinePeriods={project.getUnarcPrebaselinePeriods(
                setloadingPrebaseline
              )}
            />
            <Typography color="textPrimary" variant="h6">
              Plan
            </Typography>
            <div>
              <AnalysisTable
                selectedTask={selectedTask}
                view={view}
              ></AnalysisTable>
            </div>
          </Paper>
        </div>
        <Snackbar
          title="Load PreBaseline Message"
          open={loadingPrebaseline}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="info"
            sx={{ width: "100%" }}
          >
            <div>
              <CircularProgress color="inherit" />
              Please wait while the baseline snapshot is being generated, this
              may take several minutes
            </div>
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default observer(AnalysisView);
